import { Link } from "@tanstack/react-router";
import type { OverviewResponse } from "@rua-watch/shared";
import { formatInt } from "../lib/format";

// Operational summary surfaced at the very top of the Overview. Four
// counters that answer "is anything broken right now?" — clickable so
// the operator gets one-tap drill-into the relevant page.

export function HealthRow({ health }: { health: OverviewResponse["health"] }) {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      <HealthCard
        to="/alerts"
        label="Open alerts"
        value={health.openAlerts}
        tone={health.openAlerts > 0 ? "red" : "slate"}
      />
      <HealthCard
        to="/portfolio"
        label="Critical / warning"
        value={health.criticalDomains + health.warningDomains}
        breakdown={
          health.criticalDomains > 0 || health.warningDomains > 0
            ? `${formatInt(health.criticalDomains)} critical · ${formatInt(health.warningDomains)} warning`
            : undefined
        }
        tone={health.criticalDomains > 0 ? "red" : health.warningDomains > 0 ? "amber" : "slate"}
      />
      <HealthCard
        to="/dns-monitor"
        label="DNS changes (24h)"
        value={health.dnsChanges24h}
        tone={health.dnsChanges24h > 0 ? "amber" : "slate"}
      />
      <HealthCard
        to="/portfolio"
        label="Blocklisted IPs"
        value={health.blocklistedIps}
        tone={health.blocklistedIps > 0 ? "red" : "slate"}
      />
    </div>
  );
}

function HealthCard({
  to,
  label,
  value,
  breakdown,
  tone,
}: {
  to: string;
  label: string;
  value: number;
  breakdown?: string;
  tone: "slate" | "red" | "amber";
}) {
  // Color reflects severity. `slate` = nothing to worry about; `red` =
  // active incident; `amber` = something to watch. Tone is decoupled
  // from the count so a card with `value=0` always renders quiet even
  // if the metric type is "scary" (e.g. blocklisted IPs).
  const ring = {
    slate:  "border-slate-200 dark:border-slate-800",
    red:    "border-red-200 dark:border-red-900/40",
    amber:  "border-amber-200 dark:border-amber-900/40",
  }[tone];
  const valueColor = {
    slate: "text-slate-900 dark:text-slate-100",
    red:   "text-red-700 dark:text-red-300",
    amber: "text-amber-700 dark:text-amber-300",
  }[tone];

  return (
    <Link
      to={to}
      className={`rounded-lg border bg-white dark:bg-slate-900 p-4 transition-colors hover:border-slate-300 dark:hover:border-slate-700 ${ring}`}
    >
      <div className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</div>
      <div className={`mt-1 text-2xl font-semibold tabular-nums ${valueColor}`}>{formatInt(value)}</div>
      {breakdown ? (
        <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">{breakdown}</div>
      ) : null}
    </Link>
  );
}
